/**
 * Inquiry intake.
 *
 * This is the storefront's `Consistent Help` affordance (Design Bible 9.1,
 * WCAG 3.2.6): the same form, in the same place, on every page — inline on an
 * item page and standalone at `/help`.
 *
 * The rules it has to satisfy are the packet's: bounded, keyboard complete,
 * announces its errors and its status, preserves deliberate input safely, and
 * keeps PII out of URLs, storage, and logs.
 *
 * "Preserves deliberate input safely" is the interesting one. A message someone
 * spent five minutes writing must survive a rejected submit, a rate limit, and
 * a network failure — but it must *not* survive into `sessionStorage`, because
 * it contains whatever they chose to tell us and an email address. So it lives
 * in component state for the life of the page and is cleared only on a
 * confirmed success. That is a deliberate trade: a reload loses the draft, and
 * a shared computer does not.
 */

import { type FormEvent, type ReactNode, useId, useRef, useState } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { useAnnounce } from "../components/Announcer.js";
import { ActionButton } from "../components/primitives.js";
import { emailProblem } from "../lib/address.js";
import {
  MAX_INQUIRY_BYTES,
  commandID,
  inquiryCategories,
  sendInquiry,
  type InquiryCategory
} from "../lib/commands.js";

const CATEGORY_LABELS: Readonly<Record<InquiryCategory, string>> = {
  ITEM_QUESTION: "A question about an item",
  ORDER_HELP: "Help with an order",
  GENERAL: "Something else"
};

export function InlineInquiry({
  heading,
  intro,
  physicalItemID,
  orderID
}: {
  heading: string;
  intro: string;
  physicalItemID?: string;
  orderID?: string;
}): ReactNode {
  return (
    <section aria-labelledby="inquiry-heading" className="inquiry">
      <h2 className="t-display-xs" id="inquiry-heading">
        {heading}
      </h2>
      <p className="t-body-m">{intro}</p>
      <InquiryForm key={orderID ?? physicalItemID ?? "general"} {...(orderID === undefined ? {} : { orderID })} {...(physicalItemID === undefined ? {} : { physicalItemID })} />
    </section>
  );
}

type Status =
  | { readonly kind: "idle" }
  | { readonly kind: "sending" }
  | { readonly kind: "sent"; readonly reference: string }
  | { readonly kind: "problem"; readonly detail: string };

export function InquiryForm({ physicalItemID, orderID }: { physicalItemID?: string; orderID?: string }): ReactNode {
  const { transport, csrfToken, config } = useStorefront();
  const announce = useAnnounce();
  const ids = useId();

  const [category, setCategory] = useState<InquiryCategory>(
    orderID !== undefined ? "ORDER_HELP" : physicalItemID === undefined ? "GENERAL" : "ITEM_QUESTION"
  );
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(orderID === undefined ? "" : `Please help me confirm the status of order ${orderID}.`);
  const [showProblems, setShowProblems] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  /*
   * One command id per attempt-of-the-same-intent. It is minted when the form
   * is first submitted and reused for a retry, so a double submit replays
   * core's stored result instead of opening a second inquiry. Any edit to the
   * message or the address mints a new one.
   */
  const pendingCommandID = useRef<string | null>(null);
  const emailField = useRef<HTMLInputElement>(null);
  const messageField = useRef<HTMLTextAreaElement>(null);

  const signedIn = csrfToken !== null;
  const emailIssue = signedIn ? null : emailProblem(email);
  const messageBytes = new TextEncoder().encode(message).length;
  const messageIssue =
    messageBytes === 0
      ? "Write your question before sending."
      : messageBytes > MAX_INQUIRY_BYTES
        ? `Your message is ${String(messageBytes)} bytes. The limit is ${String(MAX_INQUIRY_BYTES)}.`
        : message.trim() !== message
          ? "Remove the leading or trailing spaces before sending."
          : null;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setShowProblems(true);
    if (emailIssue !== null) {
      announce(emailIssue);
      emailField.current?.focus();
      return;
    }
    if (messageIssue !== null) {
      announce(messageIssue);
      messageField.current?.focus();
      return;
    }
    if (status.kind === "sending") return;

    pendingCommandID.current ??= commandID();
    const id = pendingCommandID.current;
    setStatus({ kind: "sending" });
    announce("Sending your message.");

    void sendInquiry(
      transport,
      {
        commandID: id,
        ...(physicalItemID === undefined ? {} : { physicalItemID }),
        ...(signedIn ? {} : { guestEmail: email.trim() }),
        category,
        message
      },
      csrfToken ?? undefined
    ).then((outcome) => {
      if (outcome.kind === "ok") {
        pendingCommandID.current = null;
        setMessage("");
        setEmail("");
        setShowProblems(false);
        setStatus({ kind: "sent", reference: outcome.value.inquiry_id });
        announce("Your message was received.");
        return;
      }
      const detail =
        outcome.kind === "rate-limited"
          ? "Too many messages have come from this connection recently. Waiting a few minutes and sending again works, and your message is still here."
          : outcome.kind === "offline"
            ? "Your message was not sent because the connection dropped. It is still here; sending again works once you are back online."
            : outcome.kind === "rejected" || outcome.kind === "conflict"
              ? outcome.detail
              : outcome.kind === "disabled"
                ? "Messages are switched off on this site right now."
                : "The server did not accept the message. It is still here, and sending again is safe.";
      setStatus({ kind: "problem", detail });
      announce(detail);
    });
  };

  if (status.kind === "sent") {
    return (
      <div className="state state--ok" role="status">
        <p className="state-title t-body-l">Your message was received.</p>
        <p className="state-detail">
          We reply by email. Nothing about this message is stored in your browser, and your address
          is not in this page&rsquo;s URL.
        </p>
        <p className="t-body-s t-secondary">
          Reference <span className="t-id">{status.reference}</span>
        </p>
        <div className="state-actions">
          <button
            className="btn btn--quiet"
            onClick={() => {
              setStatus({ kind: "idle" });
            }}
            type="button"
          >
            Write another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="form" noValidate onSubmit={submit}>
      {status.kind === "problem" ? (
        <div className="state state--stop" role="alert">
          <p className="state-title t-body-l">Your message was not sent.</p>
          <p className="state-detail">{status.detail}</p>
        </div>
      ) : null}

      <div className="field">
        <label className="t-label" htmlFor={`${ids}-category`}>
          What is this about?
        </label>
        <select
          id={`${ids}-category`}
          onChange={(event) => {
            setCategory(event.target.value as InquiryCategory);
            pendingCommandID.current = null;
          }}
          value={category}
        >
          {inquiryCategories.map((value) => (
            <option key={value} value={value}>
              {CATEGORY_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      {signedIn ? (
        <p className="t-body-s t-secondary">
          You are signed in, so we reply to the address on your account. It is not shown here and it
          is not sent from this page.
        </p>
      ) : (
        <div className="field">
          <label className="t-label" htmlFor={`${ids}-email`}>
            Your email address
          </label>
          <input
            autoComplete="email"
            {...(showProblems && emailIssue !== null
              ? { "aria-describedby": `${ids}-email-problem`, "aria-invalid": true }
              : {})}
            id={`${ids}-email`}
            maxLength={254}
            onChange={(event) => {
              setEmail(event.target.value);
              pendingCommandID.current = null;
            }}
            ref={emailField}
            type="email"
            value={email}
          />
          {showProblems && emailIssue !== null ? (
            <p className="field-problem" id={`${ids}-email-problem`}>
              {emailIssue}
            </p>
          ) : null}
        </div>
      )}

      <div className="field">
        <label className="t-label" htmlFor={`${ids}-message`}>
          Your message
        </label>
        <textarea
          {...(showProblems && messageIssue !== null
            ? { "aria-describedby": `${ids}-message-problem ${ids}-message-count` }
            : { "aria-describedby": `${ids}-message-count` })}
          {...(showProblems && messageIssue !== null ? { "aria-invalid": true } : {})}
          id={`${ids}-message`}
          onChange={(event) => {
            setMessage(event.target.value);
            pendingCommandID.current = null;
          }}
          ref={messageField}
          rows={6}
          value={message}
        />
        <p className="t-body-s t-secondary" id={`${ids}-message-count`}>
          <span className="t-id">{messageBytes}</span> of{" "}
          <span className="t-id">{MAX_INQUIRY_BYTES}</span> bytes used.
        </p>
        {showProblems && messageIssue !== null ? (
          <p className="field-problem" id={`${ids}-message-problem`}>
            {messageIssue}
          </p>
        ) : null}
      </div>

      <ActionButton
        id={`${ids}-send`}
        pending={status.kind === "sending"}
        pendingLabel="Sending…"
        type="submit"
      >
        Send message
      </ActionButton>
      <p className="t-body-s t-secondary">{config.legal.privacy}</p>
    </form>
  );
}
